import { Fragment, ReactElement } from 'react';
import styles from './PureTable.module.scss';
import WithDevice from '../WithDevice/WithDevice';
import { classes } from '../../../lib/utils/cssUtils';
import {
  AddAction,
  DeleteAction,
  DownloadAction,
  Loader,
  OpenDetails,
  RemindAction,
  ResponseActions,
  TableBadge,
} from './PureTableActions';
import { PureTableProps } from './PureTable.types';
import { Label } from '../Label/Label';
import { TABLE_LABELS } from '../../../lib/constants/labelKeys';
import { isNil } from '../../../lib/utils/isNil';
import { TableSkeleton } from '../../app/base/Table/TableSkeletons';

const isColumnReactElement = (column: any): column is ReactElement => !['string', 'number'].includes(typeof column);

const PureTable = ({
  headings,
  data,
  selected,
  dataOrder,
  mobileVariant = 'primary',
  isInitialLoad,
  emptyPlaceholdersCount = 5,
  desktopCenteredColumns,
  ...rest
}: PureTableProps) => {
  return (
    <>
      <table className={styles.table} {...rest}>
        {!!headings && (
          <thead>
            <tr>
              {headings.map((heading, index) => (
                <th
                  className={classes(styles.tableCell, {
                    [styles.centeredColumn]: desktopCenteredColumns?.includes(index),
                  })}
                  key={heading}
                >
                  <Label name={TABLE_LABELS.HEADING(heading)} />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {dataOrder.map((rowID, index) => (
            <tr
              key={rowID}
              className={classes(styles.tableRow, {
                [styles.selectedItem]: selected?.includes(rowID),
                [styles.tableRowLargerMobile]: data[rowID]?.length === 1,
              })}
            >
              {data[rowID]?.map((column, colIndex, row) => {
                const heading = headings?.[colIndex];
                const isReactElement = isColumnReactElement(column);

                return (
                  <Fragment key={`${rowID}-${isReactElement ? column.key : column}-${headings?.[colIndex]}`}>
                    <WithDevice mobile tablet>
                      {(colIndex !== 0 || mobileVariant === 'secondary') && !isNil(heading) && (
                        <td
                          className={classes(styles.tableCell, styles.mobileHead, {
                            [styles.secondaryMobile]:
                              mobileVariant === 'secondary' &&
                              ((!(colIndex % 2) && row.length !== 1) || (row.length === 1 && !(index % 2))),
                            [styles.mobileBorder]: mobileVariant === 'primary' && colIndex !== row.length - 1,
                            [styles.mobileCenter]: mobileVariant === 'secondary',
                            [styles.lastChild]: colIndex === row.length - 1 && row.length !== 1,
                          })}
                        >
                          <Label name={TABLE_LABELS.HEADING(heading)} />
                        </td>
                      )}
                    </WithDevice>
                    <td
                      data-cy={heading}
                      className={classes(styles.tableCell, {
                        [styles.mobileHighlightedItem]: colIndex === 0 && mobileVariant === 'primary',
                        [styles.secondaryMobile]:
                          mobileVariant === 'secondary' &&
                          ((!(colIndex % 2) && row.length !== 1) || (row.length === 1 && !(index % 2))),
                        [styles.mobileBorder]: mobileVariant === 'primary' && colIndex !== row.length - 1,
                        [styles.lastChild]: colIndex === row.length - 1 && row.length !== 1,
                        [styles.centeredColumn]: desktopCenteredColumns?.includes(colIndex),
                      })}
                    >
                      {column}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {isInitialLoad && <TableSkeleton length={emptyPlaceholdersCount} />}
    </>
  );
};

PureTable.DeleteAction = DeleteAction;
PureTable.AddAction = AddAction;
PureTable.ResponseActions = ResponseActions;
PureTable.RemindAction = RemindAction;
PureTable.DownloadAction = DownloadAction;
PureTable.Badge = TableBadge;
PureTable.OpenDetails = OpenDetails;
PureTable.Loader = Loader;

export default PureTable;
